import React, { useState, useEffect } from 'react';
import { CheckCircle, XCircle, Clock, AlertCircle } from 'lucide-react';
import { adminService } from '../../services/adminService';
import API_CONFIG from '../../config/api';
import { useTranslation } from '../../i18n/LocaleContext';

// 实名认证审核：待审核列表 + 证件照查看 + 通过/驳回（驳回原因展示给用户）
const AdminKyc = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('pending');
  const [kycs, setKycs] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(null);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, total_pages: 1 });

  useEffect(() => {
    fetchKycs();
  }, [filter, page]);

  const fetchKycs = async () => {
    try {
      setLoading(true);
      const data = await adminService.getKycs(filter, 50, page);
      setKycs(data.kycs || []);
      setPendingCount(Number(data.pending_count || 0));
      setPagination(data.pagination || { total: 0, total_pages: 1 });
    } catch (error) {
      console.error('Fetch kyc error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    if (!window.confirm(t('aKycApproveConfirm'))) return;
    try {
      setProcessing(id);
      await adminService.approveKyc(id);
      alert(t('aKycApprovedMsg'));
      fetchKycs();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (id) => {
    const reason = window.prompt(t('aKycRejectAsk'), '');
    if (reason === null) return;
    try {
      setProcessing(id);
      await adminService.rejectKyc(id, reason.trim());
      alert(t('aKycRejectedMsg'));
      fetchKycs();
    } catch (error) {
      alert(error.message);
    } finally {
      setProcessing(null);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-900 bg-opacity-30 border border-yellow-700', text: 'text-yellow-400', label: t('aKycPending'), icon: Clock },
      approved: { bg: 'bg-green-900 bg-opacity-30 border border-green-700', text: 'text-green-400', label: t('aKycApproved'), icon: CheckCircle },
      rejected: { bg: 'bg-red-900 bg-opacity-30 border border-red-700', text: 'text-red-400', label: t('aKycRejected'), icon: XCircle }
    };
    const badge = badges[status];
    const Icon = badge.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const Photo = ({ src, label }) => (
    src ? (
      <a href={API_CONFIG.getImageUrl(src)} target="_blank" rel="noreferrer" className="block">
        <img src={API_CONFIG.getImageUrl(src)} alt={label} className="h-12 w-[68px] rounded object-cover ring-1 ring-gray-600 hover:ring-brand-500" />
      </a>
    ) : (
      <span className="block h-12 w-[68px] rounded bg-gray-700" />
    )
  );

  const filters = [
    { key: 'all', activeCls: 'bg-brand-500 text-white', label: t('aKycAll') },
    { key: 'pending', activeCls: 'bg-brand-500 text-white', label: t('aKycPending') },
    { key: 'approved', activeCls: 'bg-brand-500 text-white', label: t('aKycApproved') },
    { key: 'rejected', activeCls: 'bg-brand-500 text-white', label: t('aKycRejected') }
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-white">{t('aKycTitle')}</h1>
        <p className="text-gray-400 mt-1">{t('aKycSubtitle')}</p>
      </div>

      {pendingCount > 0 && (
        <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-6">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-gray-400" />
            <p className="text-sm text-gray-400">
              <strong>{t('aKycPendingCount', { n: pendingCount })}</strong> {t('aKycPendingSuffix')}
            </p>
          </div>
        </div>
      )}

      <div className="bg-gray-800 rounded-xl shadow-sm p-4 mb-6 border border-gray-700">
        <div className="flex flex-wrap gap-3">
          {filters.map((f) => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setPage(1); }}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === f.key ? f.activeCls : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-gray-800 rounded-xl shadow-sm overflow-hidden border border-gray-700">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900 border-b border-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">ID</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycUser')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycRealName')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycPhone')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycIdPhotos')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycStatus')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycDate')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase">{t('aKycActions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">{t('aKycLoading')}</td>
                </tr>
              ) : kycs.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-8 text-center text-gray-400">{t('aKycEmpty')}</td>
                </tr>
              ) : kycs.map((kyc) => (
                <tr key={kyc.id} className="hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-300">#{kyc.id}</td>
                  <td className="px-6 py-4 text-sm text-white">{kyc.username}</td>
                  <td className="px-6 py-4 text-sm font-medium text-white">{kyc.real_name}</td>
                  <td className="px-6 py-4 text-sm text-gray-300 font-mono">{kyc.phone}</td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Photo src={kyc.id_front} label={t('kycIdFront')} />
                      <Photo src={kyc.id_back} label={t('kycIdBack')} />
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    {getStatusBadge(kyc.status)}
                    {kyc.status === 'rejected' && kyc.reject_reason && (
                      <p className="mt-1 max-w-40 text-xs text-gray-500">{kyc.reject_reason}</p>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-300">{new Date(kyc.created_at).toLocaleString('vi-VN')}</td>
                  <td className="px-6 py-4">
                    {kyc.status === 'pending' && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleApprove(kyc.id)}
                          disabled={processing === kyc.id}
                          className="px-3 py-1 bg-green-500 text-white rounded-lg text-xs hover:bg-green-600 disabled:opacity-50"
                        >
                          {t('aKycApprove')}
                        </button>
                        <button
                          onClick={() => handleReject(kyc.id)}
                          disabled={processing === kyc.id}
                          className="px-3 py-1 bg-red-500 text-white rounded-lg text-xs hover:bg-red-600 disabled:opacity-50"
                        >
                          {t('aKycRejected')}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {pagination.total > 0 && <div className="mt-4 flex items-center justify-between text-sm text-gray-400"><span>{t('aKycPaginationTotal', { total: pagination.total, page, pages: pagination.total_pages })}</span><div className="flex gap-2"><button onClick={() => setPage(page - 1)} disabled={page === 1} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aKycPrev')}</button><button onClick={() => setPage(page + 1)} disabled={page >= pagination.total_pages} className="rounded bg-gray-700 px-3 py-2 disabled:cursor-not-allowed disabled:opacity-50">{t('aKycNext')}</button></div></div>}
    </div>
  );
};

export default AdminKyc;
